import { use, useCallback, useEffect, useMemo, useRef } from 'react';

import path from 'path';

import { Stack, Typography } from '@mui/material';
import {
	GridCallbackDetails,
	GridColDef,
	GridRowSelectionModel,
	useGridApiRef,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { ParamKeyValuePair, useSearchParams } from 'react-router-dom';
import useSWR, { KeyedMutator } from 'swr';

import {
	ActionButton,
	Card,
	DataGrid,
	DataGridToolbar,
	MultiSelectBar,
	type RowAction,
	useColDefs,
} from '~components';
import ActionButtonStyle from '~components/buttons/actionButtonStyle';
import VectorImage from '~components/media/vectorImage';
import { generalInformation, pagePathSegments, queryParameters } from '~constants';
import { DataGridToolBarFilterType, StatusVariant, UserRoleEnum } from '~enums';
import KeyTypeEnum from '~enums/keyTypeEnum';
import KeysListType from '~features/keys/interfaces/keysListType';
import SelectedKeysContext from '~features/keys/providers/selectedKeysContext';
import KeysService from '~features/keys/services/keysService';
import useAuthorize from '~hooks/useAuthorize';
import { OffsetPagedResults } from '~interfaces';
import Key from '~interfaces/key';
import { cancellationMiddleware } from '~lib/swr';
import { getService } from '~services/serviceFactory';

import Dimens from '../../../../themes/dimens';

const service = getService(KeysService);
const persistenceKey = 'keys';

interface KeysDataGridProps {
	showExportButton?: boolean;
	overwrittenClientId?: string;
	enableAssignKey?: boolean;
	enableUnassignKey?: boolean;
	showTitle: boolean;
	keysListType?: KeysListType;
	enableMultiSelect?: boolean;
	showResourceGroupFilter?: boolean;
	/**
	 * @param mutate Mutate object that's to be called to ensure the data grid is updated.
	 * @param clearSelection Call to clear the datagrid selection.
	 */
	onAssignKeys?: (
		mutate: KeyedMutator<OffsetPagedResults<Key>>,
		clearSelection: () => void
	) => void;
	/**
	 * @param mutate Mutate object that's to be called to ensure the data grid is updated.
	 * @param clearSelection Call to clear the datagrid selection.
	 */
	onUnassignKeys?: (
		mutate: KeyedMutator<OffsetPagedResults<Key>>,
		clearSelection: () => void
	) => void;
	onChangeResourceGroupForKeys?: (
		mutate: KeyedMutator<OffsetPagedResults<Key>>,
		clearSelection: () => void
	) => void;
	handleExportButtonClicked?: () => void;
}

const KeysDataGrid = ({
	showExportButton = false,
	overwrittenClientId,
	enableAssignKey = false,
	enableUnassignKey = false,
	enableMultiSelect = false,
	showResourceGroupFilter = false,
	showTitle = false,
	keysListType = 'assigned',
	onAssignKeys,
	onUnassignKeys,
	onChangeResourceGroupForKeys,
	handleExportButtonClicked,
}: KeysDataGridProps) => {
	const apiRef = useGridApiRef();
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();
	const selectedKeysContext = use(SelectedKeysContext);
	const auth = useAuthorize();
	const isSuperAdmin = auth.authorize([UserRoleEnum.SuperAdmin]);

	const includeOrganisationIdInLink =
		keysListType == 'unassigned' ||
		keysListType == 'assignedInternal' ||
		overwrittenClientId != undefined;

	const isInternal = keysListType == 'assignedInternal';
	const resourceGroupFilterOrganisationId =
		isInternal ? searchParams.get(queryParameters.OrganisationId) : overwrittenClientId;
	const keyCacheRef = useRef<Map<string, Key>>(new Map());

	const {
		data,
		error,
		isLoading,
		isValidating,
		mutate: mutateKeys,
	} = useSWR(
		service.getKeysCacheKey(searchParams, overwrittenClientId, keysListType),
		(_args: (string | URLSearchParams | undefined)[], options: { signal?: AbortSignal }) => {
			const page = searchParams.get(queryParameters.Page) ?? '1';
			const pageSize = searchParams.get(queryParameters.PageSize) ?? '10';
			const searchQuery = searchParams.get(queryParameters.SearchQuery) ?? undefined;
			const keyType = searchParams.get(queryParameters.KeyType) ?? undefined;
			const resourceGroupId = searchParams.get(queryParameters.ResourceGroupId) ?? undefined;
			const filterOrganisationId = searchParams.get(queryParameters.OrganisationId) ?? undefined;
			const externalOverwrittenClientId =
				keysListType === 'unassigned' ?
					generalInformation.manufacturingOrganisationId
				:	overwrittenClientId;

			const organisationId =
				keysListType == 'assignedInternal' ? filterOrganisationId : externalOverwrittenClientId;

			return service.getKeys({
				page: page,
				pageSize: pageSize,
				searchQuery: searchQuery,
				keyType: keyType,
				resourceGroupId: resourceGroupId,
				organisationId: organisationId,
				signal: options.signal,
			});
		},
		{
			keepPreviousData: true,
			use: [cancellationMiddleware],
		}
	);

	// Derive the footer row count straight from the fetched data instead of a
	// separate state synced via `onSuccess`. The previous value is retained
	// while a new page is loading (`data?.total` momentarily undefined) so the
	// count never desyncs from the rendered rows or flickers to "0-0 of 0".
	const rowCountRef = useRef<number>(0);
	if (data?.total !== undefined) {
		rowCountRef.current = data.total;
	}
	const totalCount = rowCountRef.current;

	useEffect(() => {
		if (!data?.results) return;

		for (const key of data.results) {
			keyCacheRef.current.set(key.id, key);
		}
	}, [data]);

	useEffect(() => {
		apiRef.current?.setRowSelectionModel({
			type: 'include',
			ids: new Set(selectedKeysContext.selectedKeys.map((key) => key.id)),
		});
	}, [selectedKeysContext.selectedKeys]);

	const isVirtualKeyRow = useCallback(
		(row: { type?: KeyTypeEnum | null }) => row.type === KeyTypeEnum.VirtualKey,
		[]
	);

	const clearSelection = useCallback(() => {
		// sets id 0 to unselected, and then resets the rest of the rows with the second flag
		apiRef?.current?.selectRow(0, false, true);
	}, [apiRef]);

	const handleRowSelectionModelChange = useCallback(
		(model: GridRowSelectionModel, details: GridCallbackDetails) => {
			// Missing reason means it was our own intervention, and we can ignore it to prevent infinite loop
			if (!details.reason) return;

			const newKeys = Array.from(model.ids)
				.map((id) => keyCacheRef.current.get(id.toString()))
				.filter(Boolean) as Key[];

			selectedKeysContext.replaceSelectedKeys(newKeys);
		},
		[selectedKeysContext]
	);

	const colDefs = useColDefs<Key>();
	const columns = useMemo((): GridColDef<Key>[] => {
		return [
			colDefs.id(path.join(pagePathSegments.Keys, ':id', pagePathSegments.General), (value: Key) =>
				includeOrganisationIdInLink ?
					[
						[
							queryParameters.OverwrittenClientId,
							overwrittenClientId ??
								value.organisationId ??
								generalInformation.manufacturingOrganisationId,
						],
					]
				:	([] as ParamKeyValuePair[])
			),
			colDefs.name(
				true,
				path.join(pagePathSegments.Keys, ':id', pagePathSegments.General),
				(value: Key) => {
					return includeOrganisationIdInLink ?
							[
								[
									queryParameters.OverwrittenClientId,
									overwrittenClientId ??
										(value as Key).organisationId ??
										generalInformation.manufacturingOrganisationId,
								],
							]
						:	([] as ParamKeyValuePair[]);
				}
			),
			colDefs.keyType((value: Key) => value.type),
			colDefs.serialNumber(),
			...(isInternal ?
				[colDefs.organisationName(true, (value: Key) => value.organisationId ?? '')]
			:	[]),
			colDefs.resourceGroup(),
			...(enableUnassignKey || enableAssignKey ?
				[
					colDefs.rowActions((row) => {
						const actions: RowAction[] = [];
						const currentKey = keyCacheRef.current.get(row.id.toString());
						const isVirtualKey = isVirtualKeyRow(row);
						if (isVirtualKey) {
							return actions;
						}
						if (enableUnassignKey) {
							actions.push({
								key: 'unassign-key',
								variant: StatusVariant.Destructive,
								icon: <VectorImage name='icon/unlink' />,
								label: t('unassign', { ns: 'actions' }),
								disabled: isVirtualKey,
								onClick: () => {
									selectedKeysContext.updateSingleKey(currentKey);
									onUnassignKeys?.(mutateKeys, clearSelection);
								},
							});
						}
						if (enableAssignKey) {
							actions.push({
								key: 'assign-key',
								icon: <VectorImage name='icon/link' />,
								label: t('assign', { ns: 'actions' }),
								disabled: isVirtualKey,
								onClick: () => {
									selectedKeysContext.updateSingleKey(currentKey);
									onAssignKeys?.(mutateKeys, clearSelection);
								},
							});
						}
						if (
							isSuperAdmin &&
							enableUnassignKey &&
							currentKey?.organisationId !== generalInformation.manufacturingOrganisationId
						) {
							actions.push({
								key: 'change-resource-group-for-key',
								icon: <VectorImage name='icon/swap' />,
								label: t('changeResourceGroup', { ns: 'actions' }),
								onClick: () => {
									selectedKeysContext.updateSingleKey(currentKey);
									onChangeResourceGroupForKeys?.(mutateKeys, clearSelection);
								},
							});
						}
						return actions;
					}),
				]
			:	[]),
		];
	}, [
		clearSelection,
		colDefs,
		enableAssignKey,
		enableUnassignKey,
		includeOrganisationIdInLink,
		isInternal,
		isVirtualKeyRow,
		mutateKeys,
		onAssignKeys,
		onChangeResourceGroupForKeys,
		onUnassignKeys,
		overwrittenClientId,
		isSuperAdmin,
		showResourceGroupFilter,
		t,
	]);

	const multiSelectBarActions = [
		...(enableAssignKey ?
			[
				<ActionButton
					key='multiAssign'
					startIcon={<VectorImage name='icon/link' />}
					label={t('assign', { ns: 'actions' })}
					style={ActionButtonStyle.Text}
					onClick={() => onAssignKeys?.(mutateKeys, clearSelection)}
				/>,
			]
		:	[]),
		...(enableUnassignKey ?
			[
				<ActionButton
					key='multiChangeResourceGroup'
					startIcon={<VectorImage name='icon/swap' />}
					label={t('changeResourceGroup', { ns: 'actions' })}
					style={ActionButtonStyle.Text}
					variant={StatusVariant.Neutral}
					onClick={() => onChangeResourceGroupForKeys?.(mutateKeys, clearSelection)}
				/>,
				<ActionButton
					key='multiUnassign'
					startIcon={<VectorImage name='icon/unlink' />}
					label={t('unassign', { ns: 'actions' })}
					style={ActionButtonStyle.Text}
					variant={StatusVariant.Destructive}
					onClick={() => onUnassignKeys?.(mutateKeys, clearSelection)}
				/>,
			]
		:	[]),
	];

	const actionBarFilters = [
		{
			type: DataGridToolBarFilterType.Search,
		},
		{
			type: DataGridToolBarFilterType.KeyType,
		},
		...(isInternal ?
			[
				{
					type: DataGridToolBarFilterType.Organisation,
				},
			]
		:	[]),
		...(isInternal || showResourceGroupFilter ?
			[
				{
					type: DataGridToolBarFilterType.ResourceGroup,
					organisationId: resourceGroupFilterOrganisationId ?? undefined,
					isInternal: isInternal,
				},
			]
		:	[]),
	];

	const actionBarActions = [
		...(showExportButton ?
			[
				{
					id: 'export',
					label: t('export', { ns: 'actions' }),
					variant: StatusVariant.Brand,
					onClick: handleExportButtonClicked,
					icon: <VectorImage name='icon/export' />,
				},
			]
		:	[]),
	];

	return (
		<Card>
			<Stack spacing={Dimens.spacingXl}>
				{showTitle && (
					<Typography variant='labelLg'>{t('key.labels.plural', { ns: 'entities' })}</Typography>
				)}
				{keysListType == 'assignedInternal' && (
					<Typography variant='labelLg'>
						{/* TODO: Temporary solution, because now we can't show assigned keys */}
						{/* {t('key.labels.assignedKeys', { ns: 'entities' })} */}
						{t('general.all', { ns: 'entities' })}{' '}
						{t('key.labels.plural', { ns: 'entities' }).toLowerCase()}
					</Typography>
				)}
				{keysListType == 'unassigned' && (
					<Typography variant='labelLg'>
						{t('key.labels.unassignedKeys', { ns: 'entities' })}
					</Typography>
				)}

				<DataGridToolbar filters={actionBarFilters} actions={actionBarActions} />
				<DataGrid
					apiRef={apiRef}
					getRowId={(row) => row.id}
					getRowClassName={(params) =>
						isVirtualKeyRow(params.row as { type?: KeyTypeEnum | null }) ? 'virtual-key-row' : ''
					}
					persistenceKey={persistenceKey}
					editMode='row'
					autoHeight
					disableRowSelectionOnClick
					error={error != null}
					loading={isLoading || isValidating}
					columns={columns}
					rows={data?.results ?? []}
					rowCount={totalCount}
					checkboxSelection={enableMultiSelect}
					isRowSelectable={(params) =>
						enableMultiSelect ? !isVirtualKeyRow(params.row as { type?: KeyTypeEnum | null }) : true
					}
					onRowSelectionModelChange={handleRowSelectionModelChange}
					disableRowSelectionExcludeModel
					keepNonExistentRowsSelected
					paginationModel={{
						page: Number(searchParams.get(queryParameters.Page)),
						pageSize: Number(searchParams.get(queryParameters.PageSize)),
					}}
				/>
				{(selectedKeysContext.selectedKeys?.length ?? 0) > 0 && (
					<MultiSelectBar
						label={t('xSelected', {
							ns: 'table',
							count: Number(selectedKeysContext.selectedKeys?.length ?? 0),
						})}
						actions={multiSelectBarActions}
					/>
				)}
			</Stack>
		</Card>
	);
};
export default KeysDataGrid;
